import {useLayoutEffect, useRef} from "react";
import {useObserver, useRerender} from "./CommonReact";
import {classNameScrollbar, remPixels, tooltipIfNeeded} from "./CommonHtml";
import {funcOrValOr, isInRange} from "./CommonJS";

// cols: [{ h?: header, f?: field, d?(index, iCol, col, rowData, cellData): display func, className?, classNameHeader?, props?, noSort?: header not clickable }]

export function UIVirtualTable({rows, styleHeight, rowHeight = 1.8, colGap = 0.25, cols, colHeaderRenderer, colGrow, rowRenderer, oInterface,
                                   onRowClick, onRowDoubleClick, onClickCell, onMouseEnterCell, onSizing, onClickHeader, overscanRowCount = 3, scrollToIndex,
                                   className, classNameGrid, classNameRow, classNameCol, propsRow, dataTest}) {
    const rerender = useRerender();
    const vals = useRef({sclTop: 0, rowHeights: [], startIndex: 0, stopIndex: 0, rowsFit: 0, hTm: {}}).current;
    const rTbl = useRef();
    vals.rows = rows;

    function checkMoveScroll(scrollToIndex, pxRow, fRowsFit) {
        delete vals.scrollToIndex;
        const fStartIndex = rTbl.current.scrollTop / pxRow;

        if (scrollToIndex < fStartIndex)
            vals.startIndex = scrollToIndex;
        else if (scrollToIndex >= fStartIndex + (fRowsFit |0))
            vals.startIndex = scrollToIndex - (fRowsFit |0) + 1;
        else
            return;

        if (rTbl.current.scrollHeight > rTbl.current.clientHeight)          // initial layout?
            rTbl.current.scrollTop = vals.startIndex * pxRow;
        else
            vals.hTm.scTop = setTimeout(() => rTbl.current.scrollTop = vals.startIndex * pxRow);     // DOM has not yet applied a scroll - delay a bit
    }

    function calcSizes(cmd) {
        const pxRow = rowHeight * remPixels();
        const gridRect = rTbl.current.getBoundingClientRect();
        const fRowsFit = gridRect.height / pxRow;
        vals.startIndex = rTbl.current.scrollTop / pxRow | 0;
        if (vals.scrollToIndex !== undefined)
            checkMoveScroll(vals.scrollToIndex, pxRow, fRowsFit);
        vals.stopIndex = vals.startIndex + Math.ceil(fRowsFit);
        vals.sclBarWidth = rTbl.current.offsetWidth - rTbl.current.clientWidth;
         //dbg && console.log("calcSizes ", {cmd, scrollToIndex, start: vals.startIndex, stop: vals.stopIndex, fRowsFit: (fRowsFit*10|0)/10, scrlBarWidth: vals.sclBarWidth, pxHeight: (gridRect.height*10|0)/10});
        if (!(vals.msLastScrollRecalc + 1000 > Date.now()) &&
            (vals.rows?.length >= fRowsFit) !== !!vals.sclBarWidth) {     // if bar needed but not there - recalc after bar appears...
            vals.msLastScrollRecalc = Date.now();                         // avoid re-layout loops
            vals.hTm.recalc = setTimeout(calcSizes);
        }

        const doOnSizing = () => onSizing?.({gridRect, startIndex: vals.startIndex, fRowsFit, overscanRowCount});

        if (cmd !== "inRender") {
            doOnSizing();
            rerender();
        } else if (onSizing)
            vals.hTm.sz = setTimeout(doOnSizing);
    }

    useObserver(ResizeObserver, rTbl, "height", calcSizes, {debounceMs: 100});

    useLayoutEffect(() => {
        if (rTbl.current?.scrollHeight)
            calcSizes("layoutEffect");
        if (oInterface) Object.assign(oInterface, {
            getVisibleRowElements: () => ({elements: [...rTbl.current.children[0].children[0].children], index: vals.startIndex})
        });
        return () => {
            Object.values(vals.hTm).forEach(clearTimeout);
        }
    }, [rTbl]);

    if (isInRange(scrollToIndex, 0, rows?.length -1) && scrollToIndex !== vals._scrollToIndex) {
        vals.scrollToIndex = vals._scrollToIndex = scrollToIndex;
        if (rTbl.current) calcSizes("inRender")
    }

    const style = {height: rowHeight +"rem", width: "100%"};
    const rowStyle = {display: "flex", whiteSpace: "nowrap", overflow: "hidden", gap: colGap+"rem", alignItems: "center"};
    const colStyles = cols?.map((c, iCol) => ({
        flex: c.flex ?? `${c.wConst? 0 : colGrow?.[iCol] ?? c.w ?? 1} 1 ${c.wConst? c.wConst+"rem" : "0"}`,
        minWidth: 0,
        ...c.style
    }));


    if (!rowRenderer) rowRenderer = ({style, ...passParams}) => (
        <div key={passParams.index} className={funcOrValOr(classNameRow, passParams, "")}
             style={{...style, ...rowStyle}}
             onClick={onRowClick && (event => onRowClick({event, ...passParams}))}
             onDoubleClick={onRowDoubleClick && (event => onRowDoubleClick({event, ...passParams}))}
             {...propsRow}>
            {cols.map((col, iCol) => {
                const cellData = passParams.rowData[col.f];
                const cellParams = {...passParams, iCol, col, cellData};
                return (
                    <div key={iCol}
                         style={colStyles[iCol]}
                         className={`${funcOrValOr(classNameCol, cellParams, "")} ${col.className}`}
                         onMouseEnter={col.onMouseEnter || onMouseEnterCell?
                             (event => (col.onMouseEnter || onMouseEnterCell)({event, ...cellParams})) : tooltipIfNeeded}
                         onClick={onClickCell && (event => onClickCell({event, ...cellParams}))}
                         {...col.props}>
                        {col.d? col.d(cellParams) : cellData}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div {...styleHeight && {style: {height: `${styleHeight}px`}}}
             className={`flex flex-col w-full ${!className?.match(/(^| )h-/) && "h-full"} ${className}`}>
            {cols &&
                <div style={{flexShrink: 0, ...style, ...rowStyle}}>
                    {cols.map((col, iCol) =>
                        <div key={iCol} style={{...colStyles[iCol], ...onClickHeader && !col.noSort && {cursor: "pointer"}}} className={col.classNameHeader}
                             onClick={onClickHeader && !col.noSort && (event => onClickHeader({event, iCol, col}))}>
                            {colHeaderRenderer?.({iCol, col}) ?? col.h}
                        </div>)
                    }
                    {!!vals.sclBarWidth && <div style={{width: `${vals.sclBarWidth}px`, marginRight: (-colGap) + "rem"}}/>}
                </div>
            }
            <div data-test={`${dataTest}-ui-virtual-table`} ref={rTbl} className={classNameGrid ?? classNameScrollbar} onScroll={calcSizes}
                 style={{flexGrow: 1, width: "100%", position: "relative", overflow: "hidden auto"}}>
                {//rTbl.current &&
                    <div style={{height: `${rows?.length * rowHeight}rem`}}>
                        <div style={{position: "absolute", top: `${vals.startIndex * rowHeight}rem`, width: "100%"}}>
                            {rows?.slice(vals.startIndex, vals.stopIndex + overscanRowCount)
                                .map((rowData, index) => rowRenderer({style, rowData, index: vals.startIndex + index}))}
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}
